import api from './api';
import { t as tr } from '../i18n';

// ============================================
// Generischer Kommentar-Service für Pro-Events
// und News-Artikel.
//
// Backend-Endpunkte:
//   GET    /api/comments/:entityType/:entityId?limit=&offset=   (public)
//   GET    /api/comments/replies/:commentId                     (public)
//   GET    /api/comments/:entityType/:entityId/count            (public)
//   POST   /api/comments/:entityType/:entityId                  (auth)
//   DELETE /api/comment/:commentId                              (auth, Autor)
//
// ALLE Felder werden defensiv/nullable geparst.
// ============================================

export type CommentEntityType = 'pro-event' | 'news-article' | 'post' | 'dart-room';

/**
 * Einrückung für verschachtelte Antworten (2026-09-10, Marcel: unbegrenzte
 * Tiefe). Tiefe 1 steht direkt im Thread (der Thread-Container rückt bereits
 * ein), ab Tiefe 2 rückt jede Ebene um REPLY_INDENT_STEP_PX weiter ein.
 * Damit endlose Ketten auf dem Handy nicht ins Nichts wandern, ist die
 * sichtbare Einrückung bei REPLY_INDENT_MAX_LEVELS Stufen gedeckelt — die
 * Daten bleiben trotzdem beliebig tief.
 * Pure Funktion: liefert die linke Einrückung in Pixeln.
 */
export const REPLY_INDENT_STEP_PX = 20;
export const REPLY_INDENT_MAX_LEVELS = 4;

export const replyIndentPx = (depth: number | null | undefined): number => {
  const d = Math.max(0, Math.floor(depth ?? 0));
  if (d <= 1) return 0;
  return Math.min(d - 1, REPLY_INDENT_MAX_LEVELS) * REPLY_INDENT_STEP_PX;
};

export interface EntityComment {
  id: string;
  entityType?: string | null;
  entityId?: string | null;
  parentCommentId?: string | null;
  depth: number;
  authorId?: string | null;
  authorName: string;
  authorAvatarUrl?: string | null;
  content: string;
  replyCount: number;
  isDeleted?: boolean | null;
  createdAt?: number | null;
  updatedAt?: number | null;
  replies?: EntityComment[];
  /** Likes (2026-08-27, additiv — alte Backends liefern die Felder nicht) */
  likeCount?: number;
  likedByMe?: boolean;
}

export interface EntityCommentsResponse {
  comments: EntityComment[];
  total: number;
  totalAll: number;
  limit: number;
  offset: number;
}

export interface EntityRepliesResponse {
  replies: EntityComment[];
  total: number;
}

class CommentService {
  private readonly apiBaseUrl: string;

  constructor() {
    this.apiBaseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');
  }

  // --------------------------------------------
  // API Calls
  // --------------------------------------------

  /**
   * GET /api/comments/:entityType/:entityId
   * Lädt Top-Level-Kommentare inkl. der ersten Antworten (max. 3).
   */
  async getComments(
    entityType: CommentEntityType,
    entityId: string,
    params: { limit?: number; offset?: number } = {}
  ): Promise<EntityCommentsResponse> {
    const { limit = 20, offset = 0 } = params;
    if (!entityId) return { comments: [], total: 0, totalAll: 0, limit, offset };
    try {
      const query = new URLSearchParams();
      query.set('limit', String(limit));
      query.set('offset', String(offset));
      const response = await api.get(
        `/api/comments/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}?${query.toString()}`
      );
      return this.parseCommentsResponse(response?.data, limit, offset);
    } catch (error) {
      console.error('Fehler beim Abrufen der Kommentare:', error);
      return { comments: [], total: 0, totalAll: 0, limit, offset };
    }
  }

  /**
   * GET /api/comments/replies/:commentId
   * Lädt alle Antworten auf einen Kommentar.
   */
  async getReplies(commentId: string): Promise<EntityRepliesResponse> {
    if (!commentId) return { replies: [], total: 0 };
    try {
      const response = await api.get(
        `/api/comments/replies/${encodeURIComponent(commentId)}`
      );
      const source = response?.data?.data ?? response?.data ?? {};
      const repliesRaw = Array.isArray(source?.replies) ? source.replies : [];
      const replies = repliesRaw
        .map((r: any) => this.parseComment(r))
        .filter(Boolean) as EntityComment[];
      return {
        replies,
        total: typeof source?.total === 'number' ? source.total : replies.length,
      };
    } catch (error) {
      console.error('Fehler beim Abrufen der Antworten:', error);
      return { replies: [], total: 0 };
    }
  }

  /**
   * GET /api/comments/:entityType/:entityId/count
   * Liefert nur die Kommentar-Anzahl (für Meta-Anzeigen).
   */
  async getCommentCount(entityType: CommentEntityType, entityId: string): Promise<number> {
    if (!entityId) return 0;
    try {
      const response = await api.get(
        `/api/comments/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}/count`
      );
      const source = response?.data?.data ?? response?.data ?? {};
      const count = source?.count;
      return typeof count === 'number' && count >= 0 ? count : 0;
    } catch (error) {
      console.error('Fehler beim Abrufen der Kommentar-Anzahl:', error);
      return 0;
    }
  }

  /**
   * POST /api/comments/:entityType/:entityId (auth)
   * Schreibt einen Kommentar oder eine Antwort. Wirft bei Fehlern.
   */
  async addComment(
    entityType: CommentEntityType,
    entityId: string,
    content: string,
    parentCommentId?: string | null
  ): Promise<EntityComment | null> {
    const body: any = { content };
    if (parentCommentId) body.parentCommentId = parentCommentId;
    const response = await api.post(
      `/api/comments/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
      body
    );
    return this.parseComment(response?.data?.data ?? response?.data);
  }

  /**
   * DELETE /api/comment/:commentId (auth, nur Autor)
   * Löscht einen eigenen Kommentar. Wirft bei Fehlern.
   */
  /** POST /api/comment/:commentId/like — Like togglen (auth required). */
  async toggleLike(commentId: string): Promise<{ liked: boolean; likeCount: number } | null> {
    try {
      const response = await api.post(`/api/comment/${encodeURIComponent(commentId)}/like`);
      const data = response?.data;
      if (data && typeof data.liked === 'boolean') {
        return { liked: data.liked, likeCount: typeof data.likeCount === 'number' ? data.likeCount : 0 };
      }
      return null;
    } catch (error) {
      console.error('Fehler beim Liken des Kommentars:', error);
      return null;
    }
  }

  async deleteComment(commentId: string): Promise<void> {
    await api.delete(`/api/comment/${encodeURIComponent(commentId)}`);
  }

  // --------------------------------------------
  // Parser (robust gegen abweichende Formate)
  // --------------------------------------------

  private parseCommentsResponse(
    payload: any,
    limit: number,
    offset: number
  ): EntityCommentsResponse {
    const source = payload?.data ?? payload ?? {};
    const commentsRaw = Array.isArray(source?.comments) ? source.comments : [];
    const comments = commentsRaw
      .map((c: any) => this.parseComment(c))
      .filter(Boolean) as EntityComment[];
    return {
      comments,
      total: typeof source?.total === 'number' ? source.total : comments.length,
      totalAll: typeof source?.totalAll === 'number' ? source.totalAll : comments.length,
      limit: typeof source?.limit === 'number' ? source.limit : limit,
      offset: typeof source?.offset === 'number' ? source.offset : offset,
    };
  }

  parseComment(data: any): EntityComment | null {
    if (!data || typeof data !== 'object') return null;
    const id = data.id || data._id || '';
    if (!id) return null;
    const repliesRaw = Array.isArray(data.replies) ? data.replies : [];
    return {
      id,
      entityType: data.entityType ?? null,
      entityId: data.entityId ?? null,
      parentCommentId: data.parentCommentId ?? null,
      depth: typeof data.depth === 'number' ? data.depth : 0,
      authorId: data.authorId ?? null,
      likeCount: typeof data.likeCount === 'number' ? data.likeCount : 0,
      likedByMe: data.likedByMe === true,
      authorName: data.authorName || tr('web.component.comment-item.unbekannt'),
      authorAvatarUrl: data.authorAvatarUrl ?? null,
      content: data.content || '',
      replyCount: typeof data.replyCount === 'number' ? data.replyCount : 0,
      isDeleted: typeof data.isDeleted === 'boolean' ? data.isDeleted : null,
      createdAt: this.parseTimestamp(data.createdAt),
      updatedAt: this.parseTimestamp(data.updatedAt),
      replies: repliesRaw
        .map((r: any) => this.parseComment(r))
        .filter(Boolean) as EntityComment[],
    };
  }

  /** createdAt kann Number (ms/sek) oder ISO-String sein – normalisiert auf ms. */
  private parseTimestamp(value: any): number | null {
    if (typeof value === 'number' && value > 0) {
      // Sekunden vs. Millisekunden
      return value < 1e12 ? value * 1000 : value;
    }
    if (typeof value === 'string' && value.trim()) {
      const t = new Date(value).getTime();
      if (!isNaN(t)) return t;
    }
    return null;
  }

  // --------------------------------------------
  // Helper
  // --------------------------------------------

  /** Relative Zeitangabe (deutsch), analog communityService.formatRelativeTime. */
  formatRelativeTime(timestamp?: number | null): string {
    if (!timestamp || timestamp <= 0) return '';
    const diff = Date.now() - timestamp;
    if (diff < 60 * 1000) return tr('web.service.comment-service.gerade-eben');
    const minutes = Math.floor(diff / (60 * 1000));
    if (minutes < 60) return tr('web.service.comment-service.vor-min', { minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return tr('web.service.comment-service.vor-std', { hours });
    const days = Math.floor(hours / 24);
    if (days < 7) return days === 1 ? tr('web.service.comment-service.vor-1-tag') : tr('web.service.comment-service.vor-tagen', { days });
    try {
      return new Date(timestamp).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (_) {
      return '';
    }
  }

  /**
   * Avatar-URL bauen: externe URLs bleiben unverändert,
   * Dateinamen werden als Thumbnail-URL aufgelöst.
   */
  buildAvatarUrl(fileName?: string | null, size: 100 | 200 | 400 = 100): string | null {
    if (!fileName || typeof fileName !== 'string' || !fileName.trim()) return null;
    const trimmed = fileName.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return trimmed;
    }
    const dotIndex = trimmed.lastIndexOf('.');
    if (dotIndex === -1) {
      return `${this.apiBaseUrl}/api/file/${trimmed}`;
    }
    const basename = trimmed.substring(0, dotIndex);
    const ext = trimmed.substring(dotIndex);
    return `${this.apiBaseUrl}/api/file/thumbnails/${basename}_${size}_${size}_thumbnail${ext}`;
  }

  /** Extrahiert eine sinnvolle Fehlermeldung (Backend liefert teils Plaintext-Strings). */
  extractErrorMessage(err: any, fallback: string): string {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data.trim();
    if (typeof data?.message === 'string' && data.message.trim()) return data.message.trim();
    if (typeof data?.error === 'string' && data.error.trim()) return data.error.trim();
    return fallback;
  }
}

export default new CommentService();
